import { Fragment, useEffect, useState } from 'react';
import type { Tenant } from '../../lib/database.types';
import { loadAllTenants } from '../../lib/tenants';
import {
  FEATURE_CATALOG,
  setFeature,
  isFeatureEnabled,
  type FeatureKey,
  type TenantFeatures,
} from '../../lib/superAdmin';
import {
  BILLING_STATUSES,
  BILLING_LABELS,
  toBillingInfo,
  summarizeBilling,
  trialEndDate,
  type BillingStatus,
  type BillingInfo,
  type BillingTone,
} from '../../lib/billing';
import { setBilling } from '../../lib/billingService';
import { loadAllTenantFeatures, adminSetTenantFeature } from '../../lib/featuresService';
import { PLANS, modulesForPlan, type PlanKey, type ModuleKey } from '../../lib/plans';
import { loadPerformance } from '../../lib/performanceService';
import { euros, percent, type TenantPerf } from '../../lib/performance';
import {
  networkKpis,
  rankByRevenue,
  restosToRelance,
  parcStatus,
  buildOdooCsv,
  COMMISSION_CENTS,
  SUBSCRIPTION_CENTS,
} from '../../lib/networkOverview';
import BroadcastComposer from './BroadcastComposer';
import NetworkCrmSection from './NetworkCrmSection';
import NetworkCommissionsChart from './NetworkCommissionsChart';
import CommissionsPanel from './CommissionsPanel';
import MonthlyBillingSection from './MonthlyBillingSection';
import QontoSddPanel from './QontoSddPanel';
import QontoReconPanel from './QontoReconPanel';
import RevenuePanel from './RevenuePanel';
import ServicesSection from './ServicesSection';
import PerformanceSection from './PerformanceSection';
import RetentionSection from './RetentionSection';
import RetentionEmailSection from './RetentionEmailSection';
import OnboardingLeadsSection from './OnboardingLeadsSection';
import WhatsAppSetup from './WhatsAppSetup';
import WhatsAppInboxSection from './WhatsAppInboxSection';
import { CrmCard } from './CrmSection';
import { loadTenantAccounts } from '../../lib/accountsService';
import { accountsForTenant, accountName, roleLabel, type TenantAccount } from '../../lib/accounts';
import { currentEmail, signOut } from '../../lib/authService';
import { setSiteStatus } from '../../lib/siteStatusService';
import { deleteTenant, createTenant, type CreateTenantResult } from '../../lib/tenantAdminService';
import { suggestSlug, isValidSlug } from '../../lib/onboardingLeads';
import {
  siteStatusOf,
  SITE_STATUS_LABELS,
  activationDate,
  buildClientsCsv,
  type SiteStatus,
} from '../../lib/siteStatus';

/**
 * Console SuperAdmin réseau Lil & Co — UI riche portée du back-office legacy.
 * Sidebar à 9 rubriques + contenu par section. Source unique Supabase (un nouveau
 * client apparaît automatiquement). Construite brique par brique :
 *   ✅ Tous les restaurants (liste + facturation)  ✅ Messages groupés (annonce réseau, brique A)
 *   ⏭ Vue d'ensemble · Performance · Rétention · CRM · WhatsApp · Campagnes (à venir)
 */

type SectionId =
  | 'sa-overview'
  | 'sa-leads'
  | 'sa-tenants'
  | 'sa-performance'
  | 'sa-retention'
  | 'sa-retention-email'
  | 'sa-customers'
  | 'sa-broadcast'
  | 'sa-whatsapp'
  | 'sa-whatsapp-inbox'
  | 'sa-services'
  | 'sa-billing'
  | 'sa-qonto'
  | 'sa-recon'
  | 'sa-revenue';

const NAV: { id: SectionId; icon: string; label: string }[] = [
  { id: 'sa-overview', icon: '📊', label: "Vue d'ensemble réseau" },
  { id: 'sa-leads', icon: '📥', label: 'Demandes' },
  { id: 'sa-tenants', icon: '🏢', label: 'Tous les restaurants' },
  { id: 'sa-performance', icon: '⚡', label: 'Performance métier' },
  { id: 'sa-retention', icon: '💎', label: 'Rétention & alertes' },
  { id: 'sa-retention-email', icon: '📧', label: 'Rétention email' },
  { id: 'sa-customers', icon: '👥', label: 'CRM réseau (tous clients)' },
  { id: 'sa-broadcast', icon: '📣', label: 'Messages groupés' },
  { id: 'sa-whatsapp', icon: '💬', label: 'WhatsApp Business' },
  { id: 'sa-whatsapp-inbox', icon: '📨', label: 'Inbox WhatsApp' },
  { id: 'sa-services', icon: '🧩', label: 'Options & services' },
  { id: 'sa-billing', icon: '💰', label: 'Facturation Lil & Co' },
  { id: 'sa-qonto', icon: '🏦', label: 'Prélèvement SEPA' },
  { id: 'sa-recon', icon: '🔗', label: 'Rapprochement' },
  { id: 'sa-revenue', icon: '💶', label: 'CA & Bénéfice' },
];

const TONE_STYLE: Record<BillingTone, string> = {
  good: 'bg-green-100 text-green-800',
  warn: 'bg-amber-100 text-amber-800',
  bad: 'bg-red-100 text-red-800',
  neutral: 'bg-slate-100 text-slate-600',
};

export default function SuperAdminConsole() {
  const [section, setSection] = useState<SectionId>('sa-tenants');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState<string | null>(null);

  useEffect(() => {
    loadAllTenants().then((list) => {
      setTenants(list);
      setLoading(false);
    });
    currentEmail().then(setAccount);
  }, []);

  const logout = async () => {
    await signOut();
    window.location.href = '/login';
  };

  return (
    <div className="flex min-h-screen w-full text-slate-800">
      {/* Sidebar sombre (look legacy) */}
      <aside className="w-64 shrink-0 text-white flex flex-col" style={{ background: '#0F172A' }}>
        <div className="px-4 py-4 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-widest opacity-50">Super admin · réseau</div>
          <div className="font-extrabold text-lg">Lil &amp; Co</div>
        </div>
        <nav className="flex-1 py-3">
          {NAV.map((n) => {
            const active = n.id === section;
            return (
              <button
                key={n.id}
                onClick={() => setSection(n.id)}
                className={
                  'w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 ' +
                  (active ? 'font-semibold' : 'opacity-75 hover:opacity-100')
                }
                style={active ? { background: 'linear-gradient(135deg, #2563EB 0%, #7C3AED 100%)' } : {}}
              >
                <span>{n.icon}</span>
                <span>{n.label}</span>
              </button>
            );
          })}
        </nav>
        <div className="px-4 py-3 border-t border-white/10 text-xs">
          <div className="rounded-md px-3 py-2 font-bold text-center" style={{ background: '#2563EB' }}>
            🌐 MODE SUPER ADMIN
          </div>
          {account ? (
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="opacity-60 truncate" title={account}>
                {account}
              </span>
              <button onClick={logout} className="underline opacity-70 shrink-0">
                Déconnexion
              </button>
            </div>
          ) : (
            <a href="/login" className="mt-2 block underline opacity-80">
              Se connecter (super admin)
            </a>
          )}
        </div>
      </aside>

      {/* Contenu */}
      <section className="flex-1 bg-slate-50 overflow-auto">
        <TopBar tenants={tenants} account={account} />
        <div className="p-6">
          {loading ? (
            <p className="opacity-70">Chargement du réseau…</p>
          ) : (
            <SectionContent section={section} tenants={tenants} onOpenSection={setSection} />
          )}
        </div>
      </section>
    </div>
  );
}

/** Barre du haut (parité legacy) : titre réseau + TenantSwitcher (bascule vers un resto). */
function TopBar({ tenants, account }: { tenants: Tenant[]; account: string | null }) {
  const [slug, setSlug] = useState('');
  const target = tenants.find((t) => t.slug === slug);
  return (
    <header className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-white px-6 py-3">
      <div>
        <h2 className="text-lg font-bold" style={{ color: '#2563EB' }}>Vue réseau Lil &amp; Co</h2>
        {account && <span className="text-xs text-slate-400">{account}</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase text-slate-400">Basculer vers un resto</span>
        <select value={slug} onChange={(e) => setSlug(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
          <option value="">Choisir un restaurant…</option>
          {tenants.map((t) => (
            <option key={t.id} value={t.slug}>{t.name}</option>
          ))}
        </select>
        {target && (
          <>
            <a href={`/r/${target.slug}/admin`} className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white" style={{ background: '#2563EB' }}>
              ⚙️ Ouvrir l'admin
            </a>
            <a href={`/r/${target.slug}/tablet`} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white">📱 Tablette</a>
            <a href={`/r/${target.slug}`} target="_blank" rel="noopener noreferrer" className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold">🌐 Site</a>
          </>
        )}
      </div>
    </header>
  );
}

function SectionContent({
  section,
  tenants,
  onOpenSection,
}: {
  section: SectionId;
  tenants: Tenant[];
  onOpenSection: (id: SectionId) => void;
}) {
  switch (section) {
    case 'sa-leads':
      return <OnboardingLeadsSection />;
    case 'sa-tenants':
      return <TenantsSection tenants={tenants} />;
    case 'sa-broadcast':
      return <BroadcastComposer tenants={tenants} />;
    case 'sa-overview':
      return <OverviewSection tenants={tenants} />;
    case 'sa-performance':
      return <PerformanceSection />;
    case 'sa-retention':
      return <RetentionSection onOpenSection={onOpenSection} />;
    case 'sa-retention-email':
      return <RetentionEmailSection />;
    case 'sa-customers':
      return <NetworkCrmSection />;
    case 'sa-whatsapp':
      return <WhatsAppSetup />;
    case 'sa-whatsapp-inbox':
      return <WhatsAppInboxSection />;
    case 'sa-services':
      return <ServicesSection tenants={tenants} />;
    case 'sa-billing':
      return <MonthlyBillingSection tenants={tenants} />;
    case 'sa-qonto':
      return <QontoSddPanel />;
    case 'sa-recon':
      return <QontoReconPanel />;
    case 'sa-revenue':
      return <RevenuePanel />;
  }
}

/** Vue d'ensemble réseau (parité legacy sa-overview) : bandeau MRR, KPIs, classement, Facturation Odoo, cross-marketing. */
function OverviewSection({ tenants }: { tenants: Tenant[] }) {
  const [perf1, setPerf1] = useState<TenantPerf[]>([]);
  const [perf7, setPerf7] = useState<TenantPerf[]>([]);
  const [perf30, setPerf30] = useState<TenantPerf[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([loadPerformance(1), loadPerformance(7), loadPerformance(30)]).then(([d1, d7, d30]) => {
      setPerf1(d1.data.tenants);
      setPerf7(d7.data.tenants);
      setPerf30(d30.data.tenants);
      setLoading(false);
    });
  }, []);

  const activeSlugs = new Set(
    tenants.filter((t) => summarizeBilling(toBillingInfo(t)).status === 'active').map((t) => t.slug),
  );
  const period = new Date().toISOString().slice(0, 7);
  const onlineSum = (arr: TenantPerf[]) => arr.reduce((s, t) => s + t.onlineOrders, 0);
  const kpis = networkKpis(perf30, activeSlugs.size);
  const subsCents = activeSlugs.size * SUBSCRIPTION_CENTS;
  const comm1 = onlineSum(perf1) * COMMISSION_CENTS;
  const comm7 = onlineSum(perf7) * COMMISSION_CENTS;
  const comm30 = kpis.onlineOrders * COMMISSION_CENTS;
  const mrr = subsCents + comm30;
  const ranking = rankByRevenue(perf30);
  const relance = restosToRelance(perf30);

  const downloadOdoo = () => {
    const csv = buildOdooCsv(perf30, period, activeSlugs);
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `odoo_facturation_${period}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <p className="opacity-70">Chargement des chiffres réseau…</p>;

  return (
    <div className="max-w-5xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl p-5 text-white" style={{ background: 'linear-gradient(135deg, #2563EB 0%, #1E40AF 100%)' }}>
        <div className="flex flex-wrap gap-8">
          <div className="basis-full text-xs font-bold uppercase opacity-70">Lil &amp; Co Services · Mode super admin</div>
          <div>
            <div className="text-xs uppercase opacity-80">MRR ce mois</div>
            <div className="text-3xl font-black">{euros(mrr)}</div>
            <div className="text-xs opacity-80">{euros(subsCents)} abos + {euros(comm30)} commissions Stripe</div>
          </div>
          <div>
            <div className="text-xs uppercase opacity-80">CA réseau ce mois</div>
            <div className="text-3xl font-black">{euros(kpis.networkRevenueCents)}</div>
            <div className="text-xs opacity-80">total reversé aux restos</div>
          </div>
          <div>
            <div className="text-xs uppercase opacity-80">Restos actifs</div>
            <div className="text-3xl font-black">{kpis.operational} / {kpis.restaurants}</div>
            <div className="text-xs opacity-80">commande payée &lt; 30j</div>
          </div>
        </div>
        <button onClick={downloadOdoo} className="rounded-xl bg-white/15 px-4 py-2 text-sm font-semibold backdrop-blur hover:bg-white/25">
          📥 Export Odoo · {period}
        </button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiCard label="📅 CA Lil & Co aujourd'hui" value={euros(comm1)} hint="commissions Stripe du jour" />
        <KpiCard label="📊 CA Lil & Co · 7 jours" value={euros(comm7)} hint="commissions sur 7j" />
        <KpiCard label="💎 MRR ce mois" value={euros(mrr)} hint={`abos ${euros(subsCents)} + commissions ${euros(comm30)}`} />
        <KpiCard label="🚀 ARR projeté" value={euros(mrr * 12)} hint="annualisé" />
      </div>

      {/* Mes revenus : commissions 0,99 € encaissé (Stripe) vs à recouvrer (sur place). */}
      <div className="mb-4">
        <CommissionsPanel />
      </div>

      <NetworkCommissionsChart days={30} />

      {relance.length > 0 && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-sm font-bold text-amber-800">⚠ {relance.length} resto(s) à relancer cette semaine</div>
          <p className="mb-2 text-xs text-amber-700">Ces clients ont une faible activité — un appel ou un coaching peut éviter le churn.</p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {relance.slice(0, 6).map((t) => (
              <div key={t.tenantId} className="rounded-lg bg-white px-3 py-2 text-sm">
                <div className="font-semibold">{t.name}</div>
                <div className="text-xs text-slate-500">{t.orders} cmd/30j · {t.orders === 0 ? 'jamais' : euros(t.revenueCents)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mb-4 overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-100 px-4 py-3">
          <div className="font-bold">Classement restaurants — 30 jours</div>
          <div className="text-xs text-slate-500">Trié par CA reversé décroissant · score automatique de santé</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">Resto</th>
              <th className="px-3 py-2">Statut</th>
              <th className="px-3 py-2 text-right">Cmd 30j</th>
              <th className="px-3 py-2 text-right">CA 30j (resto)</th>
              <th className="px-3 py-2 text-right">% en ligne</th>
              <th className="px-3 py-2 text-right">Dernière cmd</th>
            </tr>
          </thead>
          <tbody>
            {ranking.map((t) => {
              const st = parcStatus(t.orders);
              const tone = st === 'actif' ? 'bg-emerald-100 text-emerald-700' : st === 'en veille' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700';
              const stLabel = st === 'à relancer' ? '⚠ à relancer' : st === 'en veille' ? '⏸ en veille' : '✓ actif';
              return (
                <tr key={t.tenantId} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-semibold">{t.name} <span className="opacity-40">/{t.slug}</span></td>
                  <td className="px-3 py-2"><span className={'rounded-full px-2 py-0.5 text-xs font-semibold ' + tone}>{stLabel}</span></td>
                  <td className="px-3 py-2 text-right">{t.orders}</td>
                  <td className="px-3 py-2 text-right">{euros(t.revenueCents)}</td>
                  <td className="px-3 py-2 text-right">{percent(t.onlineRatio)}</td>
                  <td className="px-3 py-2 text-right text-slate-400">{t.orders === 0 ? '—' : 'récent'}</td>
                </tr>
              );
            })}
            {ranking.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">Aucune donnée sur la période.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-bold">📥 Facturation Odoo — mois {period}</div>
          <p className="mt-1 text-sm text-slate-600">Export CSV prêt à importer dans Odoo SH avec une ligne par tenant :</p>
          <ul className="mt-1 list-disc pl-5 text-sm text-slate-600">
            <li>Abonnement Lil &amp; Co Click &amp; Collect — 99 €</li>
            <li>Commission Stripe (× nb commandes en ligne)</li>
            <li>TVA 20 % · référence <code>LCC-{period}-CLIENT</code></li>
          </ul>
          <button onClick={downloadOdoo} className="mt-3 rounded-lg px-3 py-2 text-sm font-semibold text-white" style={{ background: '#2563EB' }}>
            📥 Télécharger le CSV
          </button>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="font-bold">🌐 Cross-marketing réseau</div>
          <p className="mt-1 text-sm text-slate-600">
            Consolide les clients opt-in réseau pour lancer une campagne cross-marques et augmenter la valeur perçue chez tes restos partenaires.
          </p>
          <a href="#" onClick={(e) => e.preventDefault()} className="mt-3 inline-block rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white">
            + Lancer une campagne réseau
          </a>
          <p className="mt-1 text-xs text-slate-400">Voir la rubrique 📣 Messages groupés.</p>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-3">
      <div className="text-xs uppercase opacity-50">{label}</div>
      <div className="text-2xl font-extrabold">{value}</div>
      {hint && <div className="text-xs opacity-50">{hint}</div>}
    </div>
  );
}

/** Tous les restaurants : liste + facturation + options (fonctionnel). */
function TenantsSection({ tenants }: { tenants: Tenant[] }) {
  const [features, setFeatures] = useState<Record<string, TenantFeatures>>({});
  const [billing, setBillingState] = useState<Record<string, BillingInfo>>(
    Object.fromEntries(tenants.map((t) => [t.id, toBillingInfo(t)])),
  );
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [accounts, setAccounts] = useState<TenantAccount[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [siteStatuses, setSiteStatuses] = useState<Record<string, SiteStatus>>(
    Object.fromEntries(tenants.map((t) => [t.id, siteStatusOf(t)])),
  );
  const [statusSaving, setStatusSaving] = useState<Record<string, boolean>>({});
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState<Record<string, boolean>>({});
  const [showAdd, setShowAdd] = useState(false);
  const [featStatus, setFeatStatus] = useState<Record<string, 'saving' | 'saved'>>({});
  const [featDirty, setFeatDirty] = useState<Record<string, boolean>>({});

  /** Enregistre les 4 options du resto en base (via la RPC sécurisée). Bouton « Enregistrer ». */
  const saveFeatures = async (tenantId: string) => {
    const optionKeys: FeatureKey[] = ['loyalty', 'reviews', 'campaigns', 'yumlink_controle'];
    const f = features[tenantId] ?? {};
    setFeatStatus((s) => ({ ...s, [tenantId]: 'saving' }));
    let allOk = true;
    for (const k of optionKeys) {
      const ok = await adminSetTenantFeature(tenantId, k, isFeatureEnabled(f, k));
      if (!ok) allOk = false;
    }
    if (!allOk) {
      setFeatStatus((s) => {
        const n = { ...s };
        delete n[tenantId];
        return n;
      });
      alert("Enregistrement impossible. Vérifie que « sql/33_admin_set_tenant_feature.sql » est bien lancé dans Supabase (SQL Editor), puis réessaie.");
      return;
    }
    setFeatDirty((d) => ({ ...d, [tenantId]: false }));
    setFeatStatus((s) => ({ ...s, [tenantId]: 'saved' }));
    setTimeout(() => {
      setFeatStatus((s) => {
        const n = { ...s };
        if (n[tenantId] === 'saved') delete n[tenantId];
        return n;
      });
    }, 2500);
  };

  useEffect(() => {
    loadTenantAccounts().then((res) => {
      if (res.ok) setAccounts(res.accounts);
    });
    loadAllTenantFeatures().then(setFeatures);
  }, []);

  const visibleTenants = tenants.filter((t) => !hidden.has(t.id));

  const toggleSelect = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allSelected = visibleTenants.length > 0 && visibleTenants.every((t) => selected.has(t.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(visibleTenants.map((t) => t.id)));

  const onDelete = async (t: Tenant) => {
    if (!confirm(`Supprimer définitivement « ${t.name} » et toutes ses données ? Action irréversible.`)) return;
    setDeleting((d) => ({ ...d, [t.id]: true }));
    const res = await deleteTenant(t.id);
    setDeleting((d) => ({ ...d, [t.id]: false }));
    if (!res.ok) {
      alert(res.error);
      return;
    }
    setHidden((h) => new Set(h).add(t.id));
    setSelected((s) => {
      const n = new Set(s);
      n.delete(t.id);
      return n;
    });
  };

  const bulkDelete = async () => {
    const targets = visibleTenants.filter((t) => selected.has(t.id));
    if (targets.length === 0) return;
    if (!confirm(`Supprimer définitivement ${targets.length} resto(s) et toutes leurs données ? Action irréversible.`)) return;
    for (const t of targets) {
      setDeleting((d) => ({ ...d, [t.id]: true }));
      const res = await deleteTenant(t.id);
      setDeleting((d) => ({ ...d, [t.id]: false }));
      if (res.ok) setHidden((h) => new Set(h).add(t.id));
      else alert(`${t.name} : ${res.error}`);
    }
    setSelected(new Set());
  };

  const exportCsv = () => {
    const rows = selected.size ? visibleTenants.filter((t) => selected.has(t.id)) : visibleTenants;
    const csv = buildClientsCsv(rows);
    const blob = new Blob([String.fromCharCode(0xfeff) + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `clients_yumlink_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const changeSiteStatus = async (tenant: Tenant, status: SiteStatus) => {
    const prev = siteStatuses[tenant.id] ?? siteStatusOf(tenant);
    setSiteStatuses((m) => ({ ...m, [tenant.id]: status }));
    setStatusSaving((s) => ({ ...s, [tenant.id]: true }));
    const res = await setSiteStatus(tenant.id, status);
    setStatusSaving((s) => ({ ...s, [tenant.id]: false }));
    if (!res.ok) {
      setSiteStatuses((m) => ({ ...m, [tenant.id]: prev }));
      alert(res.error);
    }
  };

  // Les toggles/formule modifient l'écran ; l'écriture en base se fait au clic « Enregistrer ».
  const toggle = (tenantId: string, key: FeatureKey) => {
    const cur = features[tenantId] ?? {};
    const next = !isFeatureEnabled(cur, key);
    setFeatures((prev) => ({ ...prev, [tenantId]: setFeature(prev[tenantId] ?? {}, key, next) }));
    setFeatDirty((d) => ({ ...d, [tenantId]: true }));
    setFeatStatus((s) => {
      const n = { ...s };
      delete n[tenantId];
      return n;
    });
  };

  /** Applique une FORMULE : pré-sélectionne les options incluses (à enregistrer ensuite). */
  const applyPlan = (tenantId: string, plan: PlanKey) => {
    const included = new Set<ModuleKey>(modulesForPlan(plan));
    const optionKeys: FeatureKey[] = ['loyalty', 'reviews', 'campaigns', 'yumlink_controle'];
    setFeatures((prev) => {
      let f = prev[tenantId] ?? {};
      for (const k of optionKeys) f = setFeature(f, k, included.has(k as ModuleKey));
      return { ...prev, [tenantId]: f };
    });
    setFeatDirty((d) => ({ ...d, [tenantId]: true }));
    setFeatStatus((s) => {
      const n = { ...s };
      delete n[tenantId];
      return n;
    });
  };

  const changeBilling = async (tenant: Tenant, status: BillingStatus) => {
    const prev = billing[tenant.id];
    const trialEndsAt = status === 'trial' ? prev?.trialEndsAt ?? trialEndDate() : null;
    const next: BillingInfo = { ...(prev ?? toBillingInfo(tenant)), status, trialEndsAt };
    setBillingState((b) => ({ ...b, [tenant.id]: next }));
    setSaving((s) => ({ ...s, [tenant.id]: true }));
    const res = await setBilling({ tenantId: tenant.id, status, trialEndsAt });
    setSaving((s) => ({ ...s, [tenant.id]: false }));
    if (!res.ok) {
      setBillingState((b) => ({ ...b, [tenant.id]: prev ?? toBillingInfo(tenant) }));
      alert(res.error);
    }
  };

  return (
    <div className="max-w-5xl">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold">Tous mes restaurants</h1>
          <p className="text-sm opacity-70">État global du réseau Lil &amp; Co · {visibleTenants.length} client(s)</p>
        </div>
        <div className="flex items-center gap-2">
          {selected.size > 0 && (
            <button
              onClick={bulkDelete}
              className="rounded-lg border border-red-300 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50"
            >
              🗑 Supprimer ({selected.size})
            </button>
          )}
          <button
            onClick={exportCsv}
            className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold hover:bg-slate-50"
          >
            📥 Export CSV{selected.size ? ` (${selected.size})` : ''}
          </button>
          <button
            onClick={() => setShowAdd(true)}
            className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            + Ajouter un resto
          </button>
        </div>
      </div>

      {showAdd && <AddRestoModal onClose={() => setShowAdd(false)} />}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} aria-label="Tout sélectionner" />
              </th>
              <th className="px-3 py-2">Resto</th>
              <th className="px-3 py-2">Statut</th>
              <th className="px-3 py-2">Activé le</th>
              <th className="px-3 py-2">Plan</th>
              <th className="px-3 py-2 text-right">MRR</th>
            </tr>
          </thead>
          <tbody>
            {visibleTenants.map((t) => {
              const info = billing[t.id] ?? toBillingInfo(t);
              const sum = summarizeBilling(info);
              const isOpen = expanded === t.id;
              const mrr = info.status === 'active' ? '99,00 €' : '0,00 €';
              const plan = t.billing_notes || sum.detail || sum.label;
              const sstatus = siteStatuses[t.id] ?? siteStatusOf(t);
              return (
                <Fragment key={t.id}>
                  <tr onClick={() => setExpanded(isOpen ? null : t.id)} className="cursor-pointer border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} aria-label={`Sélectionner ${t.name}`} />
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-semibold">
                        {t.name}
                        {sstatus === 'suspended' && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">⏸ Suspendu</span>}
                        {sstatus === 'disabled' && <span className="ml-2 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-800">⛔ Désactivé</span>}
                      </div>
                      <div className="text-xs text-slate-400">{t.address_line ? `${t.address_line}${t.city ? ', ' + t.city : ''}` : '/' + t.slug}</div>
                    </td>
                    <td className="px-3 py-2"><span className={'rounded-full px-2 py-0.5 text-xs font-semibold ' + TONE_STYLE[sum.tone]}>{info.status === 'active' ? '✓ Actif' : sum.label}</span></td>
                    <td className="px-3 py-2 text-slate-500">{activationDate(t.created_at)}</td>
                    <td className="px-3 py-2 text-slate-600">{plan}</td>
                    <td className="px-3 py-2 text-right font-semibold">{mrr}</td>
                  </tr>
                  {isOpen && (
                    <tr className="border-t border-slate-100 bg-slate-50/60">
                      <td colSpan={6} className="px-3 py-3">
                        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm">
                          <label className="opacity-70">Site :</label>
                          <span className={'rounded-full px-2 py-0.5 text-xs font-semibold ' + (sstatus === 'active' ? 'bg-emerald-100 text-emerald-700' : sstatus === 'suspended' ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800')}>
                            {SITE_STATUS_LABELS[sstatus]}
                          </span>
                          {sstatus === 'active' ? (
                            <>
                              <button disabled={statusSaving[t.id]} onClick={() => changeSiteStatus(t, 'suspended')} className="rounded-md border border-amber-300 px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50">⏸ Suspendre</button>
                              <button disabled={statusSaving[t.id]} onClick={() => { if (confirm(`Désactiver définitivement le site de ${t.name} ? (réversible en base)`)) changeSiteStatus(t, 'disabled'); }} className="rounded-md border border-red-300 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">⛔ Désactiver</button>
                            </>
                          ) : (
                            <button disabled={statusSaving[t.id]} onClick={() => changeSiteStatus(t, 'active')} className="rounded-md px-2.5 py-1 text-xs font-semibold text-white disabled:opacity-50" style={{ background: '#16A34A' }}>✅ Réactiver</button>
                          )}
                          {statusSaving[t.id] && <span className="opacity-50">enregistrement…</span>}
                          <span className="text-xs opacity-50">Suspendu/Désactivé = page maintenance côté client (admin toujours accessible).</span>
                        </div>
                        <div className="flex items-center gap-2 mb-3 text-sm">
                          <label className="opacity-70">Facturation (abonnement) :</label>
                          <select value={info.status} disabled={saving[t.id]} onChange={(e) => changeBilling(t, e.target.value as BillingStatus)} className="rounded border border-slate-300 px-2 py-1">
                            {BILLING_STATUSES.map((s) => <option key={s} value={s}>{BILLING_LABELS[s]}</option>)}
                          </select>
                          {saving[t.id] && <span className="opacity-50">enregistrement…</span>}
                          <a href={`/r/${t.slug}/admin`} className="ml-auto rounded-md px-2.5 py-1 text-xs font-semibold text-white" style={{ background: '#2563EB' }}>⚙️ Ouvrir l'admin</a>
                          <a href={`/r/${t.slug}/tablet`} className="rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white">📱 Tablette</a>
                        </div>
                        <div className="mb-3 rounded-lg border border-slate-200 bg-white p-3">
                          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                            <span className="text-sm font-semibold">🧩 Options &amp; formule</span>
                            <div className="flex items-center gap-2">
                              {featDirty[t.id] && featStatus[t.id] !== 'saving' && <span className="text-xs font-semibold text-amber-600">● non enregistré</span>}
                              {featStatus[t.id] === 'saved' && <span className="text-xs font-semibold text-emerald-600">✓ enregistré</span>}
                              <button
                                onClick={() => saveFeatures(t.id)}
                                disabled={featStatus[t.id] === 'saving'}
                                className="rounded-lg px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50"
                                style={{ background: '#16A34A' }}
                              >
                                {featStatus[t.id] === 'saving' ? 'Enregistrement…' : '💾 Enregistrer'}
                              </button>
                            </div>
                          </div>
                          <div className="mb-3 flex flex-wrap items-center gap-2">
                            <span className="text-xs text-slate-500">Appliquer une formule :</span>
                            {PLANS.map((p) => (
                              <button
                                key={p.key}
                                onClick={() => applyPlan(t.id, p.key)}
                                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold hover:bg-slate-50"
                              >
                                {p.label} · {p.priceLabel}
                              </button>
                            ))}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {(Object.keys(FEATURE_CATALOG) as FeatureKey[]).map((k) => {
                              const on = isFeatureEnabled(features[t.id] ?? {}, k);
                              return (
                                <button
                                  key={k}
                                  onClick={() => toggle(t.id, k)}
                                  className={'text-sm rounded-full px-3 py-1 border ' + (on ? 'text-white border-transparent' : 'text-slate-600 hover:bg-slate-50')}
                                  style={on ? { background: '#2563EB' } : {}}
                                >
                                  {on ? '✓ ' : ''}{FEATURE_CATALOG[k]}
                                </button>
                              );
                            })}
                          </div>
                          <p className="mt-2 text-xs text-slate-400">
                            Choisis une formule ou coche/décoche les options, puis clique <b>💾 Enregistrer</b>. (Nécessite que <code>sql/33</code> soit lancé en base.)
                          </p>
                        </div>
                        <details className="mb-2">
                          <summary className="cursor-pointer text-sm font-semibold opacity-70">📇 Fiche partenaire (gérant)</summary>
                          <div className="mt-2"><CrmCard tenant={t} /></div>
                        </details>
                        <details>
                          <summary className="cursor-pointer text-sm font-semibold opacity-70">👥 Comptes admin ({accountsForTenant(accounts, t.id).length})</summary>
                          <ul className="mt-2 flex flex-col gap-1">
                            {accountsForTenant(accounts, t.id).map((a) => (
                              <li key={a.userId} className="flex items-center justify-between text-sm border-b border-slate-100 py-1">
                                <span>{accountName(a)} <span className="opacity-50">· {a.email}</span></span>
                                <span className="text-xs rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">{roleLabel(a.role)}</span>
                              </li>
                            ))}
                            {accountsForTenant(accounts, t.id).length === 0 && <li className="text-sm opacity-50">Aucun compte rattaché.</li>}
                          </ul>
                        </details>
                        <div className="mt-3 border-t border-slate-200 pt-3">
                          <button
                            onClick={() => onDelete(t)}
                            disabled={deleting[t.id]}
                            className="rounded-md border border-red-300 px-2.5 py-1 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                          >
                            {deleting[t.id] ? 'Suppression…' : '🗑 Supprimer ce resto'}
                          </button>
                          <span className="ml-2 text-xs opacity-50">Supprime le resto et toutes ses données (irréversible).</span>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Modale « Ajouter un resto » : crée un site manuellement (super admin → create-tenant → provision). */
function AddRestoModal({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState<'fr' | 'ch' | 'be'>('fr');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CreateTenantResult | null>(null);

  const submit = async () => {
    const s = slug.trim().toLowerCase();
    if (name.trim().length < 2) return setError('Indique le nom du resto.');
    if (!isValidSlug(s)) return setError('Adresse invalide : minuscules, chiffres et tirets (3 à 40 car.).');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) return setError('Email invalide.');
    setError(null);
    setBusy(true);
    const res = await createTenant({ name: name.trim(), slug: s, email: email.trim().toLowerCase(), country });
    setBusy(false);
    if (!res.ok) return setError(res.error ?? 'Création impossible.');
    setResult(res);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-bold">Ajouter un restaurant</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>
        {result ? (
          <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
            ✅ Site créé : <a href={result.sitePath} className="underline">{result.sitePath}</a> · <a href={result.adminPath} className="underline">admin</a>
            <div className="mt-1 text-xs">
              Identifiant : <b>{result.email}</b> · Mot de passe provisoire : <b>{result.tempPassword}</b> · {result.emailSent ? '📧 email envoyé' : '⚠️ email non envoyé'}
            </div>
            <button onClick={() => window.location.reload()} className="mt-3 rounded-lg px-3 py-1.5 text-sm font-semibold text-white" style={{ background: '#2563EB' }}>
              Rafraîchir la liste
            </button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <label className="text-sm">
              Nom du resto
              <input value={name} onChange={(e) => { setName(e.target.value); if (!slug) setSlug(suggestSlug(e.target.value)); }} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5" placeholder="Ex : Burger du Coin" />
            </label>
            <label className="text-sm">
              Adresse du site (/r/)
              <input value={slug} onChange={(e) => setSlug(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5" placeholder="burger-du-coin" />
            </label>
            <label className="text-sm">
              Email du gérant
              <input value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5" placeholder="gerant@email.fr" />
            </label>
            <label className="text-sm">
              Pays
              <select value={country} onChange={(e) => setCountry(e.target.value as 'fr' | 'ch' | 'be')} className="mt-1 w-full rounded-lg border border-slate-300 px-2 py-1.5">
                <option value="fr">France (€)</option>
                <option value="be">Belgique (€)</option>
                <option value="ch">Suisse (CHF)</option>
              </select>
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="mt-2 flex justify-end gap-2">
              <button onClick={onClose} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-semibold">Annuler</button>
              <button onClick={submit} disabled={busy} className="rounded-lg px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-50" style={{ background: '#16A34A' }}>
                {busy ? 'Création…' : 'Créer le site'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
